if (!customElements.get('shipping-rates')) {
  class ShippingRates extends HTMLElement {
      constructor() {
          super();
          this.form = this.querySelector('[data-form="shipping-rates"]');
          this.ratesList = this.querySelector('[data-shipping-rates]');
          this.button = this.querySelector('[data-form-show]');
          this.message = this.querySelector('[data-message]');
          this.useLocationButton = this.querySelector('.shipping-rates__use-location');
          this.useLocationLabel = this.useLocationButton?.querySelector('[data-use-location-label]');
          this.submitButton = this.form?.querySelector('button[type="submit"]');
          this.place = this.querySelector('[data-shipping-place]');
          this.placeText = this.querySelector('[data-shipping-location]');
          this.location = this.getAttribute('data-location');
          this.variantID = null;
          this.originalCartItems = [];
          this.isSubmitting = false;
          this.simulationCacheKey = 'unilar-shipping-sim-cache-v1';
          this.simulationCacheTtlMs = 5 * 60 * 1000;
          this.init();
      }

      getSavedAddress() {
          try {
              const fromSession = sessionStorage.getItem('unilar-address');
              if (fromSession) return JSON.parse(fromSession);
              const fromLocal = localStorage.getItem('unilar-address');
              return fromLocal ? JSON.parse(fromLocal) : null;
          } catch (error) {
              console.error('Error reading saved address:', error);
              return null;
          }
      }

      normalizeCep(value) {
          return String(value || '').replace(/\D/g, '');
      }

      formatCep(value) {
          const digits = this.normalizeCep(value);
          if (digits.length !== 8) return value;
          return `${digits.slice(0, 5)}-${digits.slice(5)}`;
      }

      syncSavedAddress(data) {
          const addressData = {
              ...data,
              updatedAt: Date.now()
          };
          const serialized = JSON.stringify(addressData);
          try {
              sessionStorage.setItem('unilar-address', serialized);
          } catch (error) {
              console.warn('Erro ao salvar CEP no sessionStorage:', error);
          }
          try {
              localStorage.setItem('unilar-address', serialized);
          } catch (error) {
              console.warn('Erro ao salvar CEP no localStorage:', error);
          }
          document.dispatchEvent(new CustomEvent('cep:updated', {
              detail: addressData
          }));
      }

      getSimulationCache() {
          try {
              const raw = sessionStorage.getItem(this.simulationCacheKey);
              return raw ? JSON.parse(raw) : {};
          } catch (error) {
              console.warn('Erro ao ler cache de simulacao:', error);
              return {};
          }
      }

      readSimulationCache(key) {
          const cache = this.getSimulationCache();
          const entry = cache[key];
          if (!entry) return null;
          if (!entry.timestamp || (Date.now() - entry.timestamp) > this.simulationCacheTtlMs) {
              delete cache[key];
              sessionStorage.setItem(this.simulationCacheKey, JSON.stringify(cache));
              return null;
          }
          return entry;
      }

      writeSimulationCache(key, entry) {
          const cache = this.getSimulationCache();
          cache[key] = entry;
          sessionStorage.setItem(this.simulationCacheKey, JSON.stringify(cache));
      }

      buildCacheKey(variantId, quantity, zip) {
          return `${variantId}|${quantity}|${zip}`;
      }

      sleep(ms) {
          return new Promise((resolve) => setTimeout(resolve, ms));
      }

      /**
       * Retry wrapper for transient network/API failures.
       * Does not retry 4xx (except 408/429) — invalid CEP should fail fast.
       */
      async withRetry(fn, { retries = 2, delayMs = 400, label = 'request' } = {}) {
          let lastError;

          for (let attempt = 0; attempt <= retries; attempt++) {
              try {
                  return await fn(attempt);
              } catch (error) {
                  lastError = error;
                  const status = error?.status;
                  const isClientError = typeof status === 'number'
                      && status >= 400
                      && status < 500
                      && status !== 408
                      && status !== 429;

                  if (isClientError || attempt === retries) break;

                  console.warn(`[shipping-rates] ${label} failed (attempt ${attempt + 1}/${retries + 1}), retrying…`, error);
                  await this.sleep(delayMs * (attempt + 1));
              }
          }

          throw lastError;
      }

      async fetchCepData(zip) {
          return this.withRetry(async () => {
              const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${zip}`);
              if (!response.ok) {
                  const error = new Error('CEP inválido. Verifique o número e tente novamente.');
                  error.status = response.status;
                  throw error;
              }
              return response.json();
          }, { retries: 2, delayMs: 400, label: 'BrasilAPI CEP' });
      }

      init() {
          if (!this.form) return;

          this.form.addEventListener("submit", (event) => this.submit(event));
          this.form.querySelector('input[name="zip-code"]')
              ?.addEventListener("input", (event) => this.maskZipCode(event));
          if (this.button) this.button.addEventListener("click", () => this.reset());
          if (this.useLocationButton) {
              this.useLocationButton.addEventListener('click', () => this.useGeolocation());
          }
          this.prefillZipFromSession();
      }

      reset() {
          this.showElement(this.form);
          this.hideElement(this.ratesList);
          this.hideElement(this.place);
          this.hideElement(this.message);
      }

      async submit(event) {
          event.preventDefault();
          if (this.isSubmitting) return;
          this.isSubmitting = true;

          const button = this.form.querySelector('button[type="submit"]');
          let restoreCart = false;

          try {
              button.disabled = true;
              button.textContent = 'Consultando...';
              this.hideElement(this.message);
              this.hideElement(this.ratesList);
              this.hideElement(this.place);

              const zip = this.form.querySelector('input[name="zip-code"]').value.replace(/\D/g, '');
              
              if (zip.length !== 8) {
                  throw new Error('CEP deve conter 8 dígitos. Verifique e tente novamente.');
              }

              let cacheKey = null;
              let variantId = null;
              let desiredQuantity = 1;

              // 🔹 PDP — usa apenas o produto atual para simulação
              if (this.location === 'PDP') {
                  const variantInput = document.querySelector('input[name="id"]');
                  variantId = variantInput.value;
                  desiredQuantity = this.getPdpQuantity(variantInput);
                  if (variantId) {
                      cacheKey = this.buildCacheKey(variantId, desiredQuantity, zip);
                      const cached = this.readSimulationCache(cacheKey);
                      if (cached && cached.rates) {
                          this.displayShippingRates(cached.rates);
                          this.setLocationLabel(cached.city, cached.state);
                          return;
                      }
                  }

                  await this.saveCurrentCart();

                  if (this.cartMatchesSimulation(variantId, desiredQuantity)) {
                      restoreCart = false;
                  } else {
                      restoreCart = true;
                      if (this.originalCartItems.length) {
                          await this.clearCart();
                      }
                      await this.simulationCart(variantId, desiredQuantity);
                  }
              }

              const savedAddress = this.getSavedAddress();
              const savedCep = this.normalizeCep(savedAddress?.cep);

              let state, cep, city;
              if (savedAddress && savedCep === zip) {
                  state = savedAddress.state || savedAddress.uf || '';
                  cep = savedAddress.cep;
                  city = savedAddress.city;
              } else {
                  const data = await this.fetchCepData(zip);
                  state = data.state;
                  cep = data.cep;
                  city = data.city;

                  const addressData = {
                      cep: this.formatCep(zip),
                      city: city || '',
                      state: state || '',
                      neighborhood: data.neighborhood || '',
                      street: data.street || ''
                  };
                  this.syncSavedAddress(addressData);
              }

              const shippingRates = await this.fetchShippingRatesFast(state, cep);

              if (this.location === 'PDP' && cacheKey) {
                  this.writeSimulationCache(cacheKey, {
                      timestamp: Date.now(),
                      rates: shippingRates,
                      cep,
                      city,
                      state
                  });
              }

              this.displayShippingRates(shippingRates);
              this.setLocationLabel(city, state);

          } catch (error) {
              console.warn(error);
              let errorMessage = error.message || 'Erro ao consultar o frete.';

              if (error?.code === 'SHIPPING_UNAVAILABLE') {
                  errorMessage = 'Não é possível enviar o pedido para o endereço selecionado. Confirme se ele está correto e tente outra vez ou selecione outro.';
              } else if (errorMessage === 'Failed to fetch') {
                  errorMessage = 'CEP inválido. Verifique o número e tente novamente.';
              }

              this.displayError(errorMessage);
          } finally {
              try {
                  if (restoreCart && this.location === 'PDP') {
                      await this.restoreOriginalCart();
                  }
              } catch (restoreError) {
                  console.error('[shipping-rates] Falha ao restaurar o carrinho:', restoreError);
              } finally {
                  if (button) {
                      button.disabled = false;
                      button.textContent = 'OK';
                  }
                  this.isSubmitting = false;
              }
          }
      }

      getPdpQuantity(variantInput) {
          const productForm = variantInput?.closest('form[data-type="add-to-cart-form"]');
          const quantityInput = productForm?.querySelector('input[name="quantity"]')
              || document.querySelector('product-form-component form[data-type="add-to-cart-form"] input[name="quantity"]');
          const quantity = Number(quantityInput?.value);

          return Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
      }

      async saveCurrentCart() {
          const res = await fetch('/cart.js');
          if (!res.ok) {
              throw new Error(`[shipping-rates] Falha ao salvar o carrinho atual (HTTP ${res.status}).`);
          }
          const data = await res.json();
          this.originalCartItems = data.items || [];
      }

      async clearCart() {
          const response = await fetch('/cart/clear.js', { method: 'POST' });
          if (!response.ok) {
              throw new Error(`[shipping-rates] Falha ao limpar o carrinho (HTTP ${response.status}).`);
          }
      }

      async restoreOriginalCart() {
          await this.clearCart();
          if (!this.originalCartItems.length) return;
          const items = this.originalCartItems.map(i => ({ id: i.variant_id, quantity: i.quantity, properties: i.properties || {} }));
          const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items })
          });
          if (!response.ok) {
              throw new Error(`[shipping-rates] Falha ao restaurar os itens do carrinho (HTTP ${response.status}).`);
          }
      }

      async simulationCart(variantId, quantity = 1) {
          const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: variantId, quantity }] })
          });
          if (!response.ok) {
              throw new Error(`[shipping-rates] Falha ao adicionar o item para simulação (HTTP ${response.status}).`);
          }
      }

      cartMatchesSimulation(variantId, quantity) {
          if (this.originalCartItems.length !== 1) return false;
          const item = this.originalCartItems[0];
          const hasProperties = item.properties && Object.keys(item.properties).length > 0;

          return String(item.variant_id) === String(variantId)
              && item.quantity === quantity
              && !hasProperties;
      }

      // 🚀 Versão simplificada — endpoint direto de shipping rates
      async fetchShippingRatesFast(province, zip) {
          const queryParams = new URLSearchParams({
              'shipping_address[zip]': zip,
              'shipping_address[country]': 'BR',
              'shipping_address[province]': province
          });

          const url = `/cart/shipping_rates.json?${queryParams.toString()}`;

          return this.withRetry(async () => {
              const response = await fetch(url);

              // 204 / 4xx no shipping_rates = sem cobertura (não erro genérico de consulta)
              if (!response.ok || response.status === 204) {
                  const status = response.status;
                  const isCoverageIssue = status === 204
                      || (status >= 400 && status < 500 && status !== 408 && status !== 429);
                  const error = new Error(
                      isCoverageIssue
                          ? 'Frete indisponível'
                          : 'Não conseguimos consultar o frete. Tente novamente.'
                  );
                  if (isCoverageIssue) error.code = 'SHIPPING_UNAVAILABLE';
                  error.status = status;
                  throw error;
              }

              const rates = await response.json();
              if (!rates?.shipping_rates?.length) {
                  // Empty rates can be transient while Shopify computes — retry, then treat as unavailable
                  const error = new Error('Frete indisponível');
                  error.code = 'SHIPPING_UNAVAILABLE';
                  error.status = 503;
                  throw error;
              }

              return rates;
          }, { retries: 2, delayMs: 500, label: 'shipping_rates' });
      }

      displayShippingRates(data) {
          const rates = data.shipping_rates || [];
          if (!rates.length) {
              const error = new Error('Frete indisponível');
              error.code = 'SHIPPING_UNAVAILABLE';
              throw error;
          }
          this.ratesList.innerHTML = rates.map(rate => {
              const hasDeadline = Boolean(rate.delivery_days?.[0]);
              const deadlineText = hasDeadline
                  ? `Em até ${rate.delivery_days[0]} dias úteis`
                  : 'Confira a disponibilidade no checkout';
              const isPaid = parseFloat(rate.price) > 0;
              const priceText = isPaid ? `R$ ${rate.price.toString().replace('.', ',')}` : 'GRÁTIS';

              return `
              <li class="shipping-rate">
                  <div class="shipping-rate__info">
                      <span class="shipping-rate__title">${rate.name}</span>
                      <span class="shipping-rate__desc">${deadlineText}</span>
                  </div>
                  <div class="shipping-rate__price${isPaid ? '' : ' is-free'}">${priceText}</div>
              </li>
              `;
          }).join('');
          this.appendPickupRate();
          this.showElement(this.ratesList);
      }

      appendPickupRate() {
          const template = this.querySelector('[data-pickup-rate-template]');
          if (!template) return;
          this.ratesList.appendChild(template.content.cloneNode(true));
      }

      maskZipCode(e) {
          let v = e.target.value.replace(/\D/g, '');
          if (v.length > 5) v = v.replace(/^(\d{5})(\d{1,3}).*/, '$1-$2');
          e.target.value = v;
      }

      displayError(msg) {
          this.message.textContent = msg;
          this.message.style.color = '#3e3e3d';
          this.showElement(this.message);
      }

      prefillZipFromSession() {
          const input = this.form?.querySelector('input[name="zip-code"]');
          if (!input) return;

          const savedAddress = this.getSavedAddress();
          if (!savedAddress) return;

          try {
              if (savedAddress?.cep) {
                  input.value = savedAddress.cep;
                  input.dispatchEvent(new Event('input', { bubbles: true }));
              }
              if (savedAddress?.city && savedAddress?.state) {
                  this.setLocationLabel(savedAddress.city, savedAddress.state);
              }
          } catch (error) {
              console.warn('Erro ao ler CEP salvo:', error);
          }
      }

      setLocationLabel(city, state) {
          if (!this.place || !this.placeText) return;
          if (!city || !state) {
              this.hideElement(this.place);
              return;
          }

          this.placeText.textContent = `${city}, ${state}`;
          this.showElement(this.place);
      }

      async useGeolocation() {
          if (!navigator.geolocation) {
              this.displayError('Geolocalização não disponível no seu navegador.');
              return;
          }

          const input = this.form.querySelector('input[name="zip-code"]');
          if (!input) return;

          this.useLocationButton.disabled = true;
          const originalText = this.useLocationLabel?.textContent || '';
          if (this.useLocationLabel) this.useLocationLabel.textContent = 'Obtendo localização...';
          this.setSubmitLoading(true);

          navigator.geolocation.getCurrentPosition(
              (position) => {
                  const { latitude, longitude } = position.coords;

                  fetch(
                      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
                      { headers: { 'User-Agent': 'unilarStore/1.0' } }
                  )
                      .then(response => response.json())
                      .then(data => {
                          const postcode = data.address?.postcode;
                          if (!postcode) throw new Error('CEP não encontrado para esta localização');

                          const cleanCEP = postcode.replace(/\D/g, '');
                          if (cleanCEP.length !== 8) {
                              throw new Error('CEP inválido obtido da localização');
                          }

                          const formattedCEP = `${cleanCEP.slice(0, 5)}-${cleanCEP.slice(5)}`;
                          input.value = formattedCEP;
                          input.dispatchEvent(new Event('input', { bubbles: true }));
                          this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                      })
                      .catch(error => {
                          console.warn('Erro no reverse geocoding:', error);
                          this.displayError('Não foi possível obter o CEP da sua localização. Digite manualmente.');
                      })
                      .finally(() => {
                          this.useLocationButton.disabled = false;
                          if (this.useLocationLabel) this.useLocationLabel.textContent = originalText;
                          this.setSubmitLoading(false);
                      });
              },
              (error) => {
                  console.warn('Erro ao obter localização:', error);
                  let errorMessage = 'Não foi possível obter sua localização.';

                  switch (error.code) {
                      case error.PERMISSION_DENIED:
                          errorMessage = 'Permissão negada. Habilite a localização no navegador.';
                          break;
                      case error.POSITION_UNAVAILABLE:
                          errorMessage = 'Localização indisponível no momento';
                          break;
                      case error.TIMEOUT:
                          errorMessage = 'Tempo esgotado ao obter localização.';
                          break;
                      default:
                          break;
                  }

                  this.displayError(errorMessage);
                  this.useLocationButton.disabled = false;
                  if (this.useLocationLabel) this.useLocationLabel.textContent = originalText;
                  this.setSubmitLoading(false);
              }
          );
      }

      setSubmitLoading(isLoading) {
          if (!this.submitButton) return;
          if (isLoading) {
              this.submitButton.disabled = true;
              this.submitButton.classList.add('is-loading');
              this.submitButton.dataset.originalText = this.submitButton.textContent;
              this.submitButton.textContent = 'OK';
              return;
          }

          this.submitButton.disabled = false;
          this.submitButton.classList.remove('is-loading');
          if (this.submitButton.dataset.originalText) {
              this.submitButton.textContent = this.submitButton.dataset.originalText;
              delete this.submitButton.dataset.originalText;
          }
      }

      showElement(el) { el?.classList.remove('hidden'); }
      hideElement(el) { el?.classList.add('hidden'); }
  }

  customElements.define('shipping-rates', ShippingRates);
}

